import React, { useState, useRef } from 'react';
import DOMPurify from 'dompurify';
import { Button, Modal, Select, Space, Typography } from 'antd';
import { EditOutlined, OrderedListOutlined } from '@ant-design/icons';
import { routeHistory } from '@nackle/biw-web-router';
import { Routes } from '../../../routes';
import { authService } from '../../../auth/authService';
import DeleteButton from '../../components/DeleteButton/DeleteButton';
import ListSearchItemsByIds from '../../components/ListSearchItemsByIds/ListSearchItemsByIds';
import { renderWithSearchWords } from '../../components/SearchBar/SearchBar';
import useDeleteCategory from '../../gql/hooks/useDeleteCategory'; 
import SearchQueryList from '../../components/SearchQueryList/SearchQueryList';
import CategoriesReorder from './CategoriesReorder/CategoriesReorder';
import useSortCategoriesMutation from '../../gql/hooks/useSortCategoriesMutation';
import { ColoredTag } from '../GenericCreateEdit/GenericElements';
let expandedRows = [];

const CategoriesListPage = () => { 
    const listPageRef = useRef(); 
    const catsReorderPage = useRef(); 
    const { deleteCategory, isDeleting } = useDeleteCategory();
    const [ showReorder, setShowReorder ] = useState( false );
    const [ savingReorder, setSavingReorder ] = useState( false );
    const [ sortCategories ] = useSortCategoriesMutation();

    const breadcrumbs = [
        { name: 'Course Studio', path: Routes.Home.buildPath() },
        { name: 'Content Taxonomy', path: Routes.ContentTaxonomyNav.buildPath() },
        { name: 'Categories' }
    ];

    const uiStrings = {
        cardTitle: 'Categories',
        createButtonTitle: 'Create New Category'
    };

    const handleDeleteClick = async ( id ) => {
        await deleteCategory( id, () => {
            listPageRef.current.reload();
        } );
    };

    const handleCreateClick = () => {
        routeHistory.push( Routes.CreateCategory.buildPath() );
    };

    const handleSetExpandedRows = ( rows ) => {
        expandedRows = rows;
    };

    const handleReorderClick = () => {
        setSavingReorder( false );
        setShowReorder( true );
    };

    const handleReorderSaveClick = async () => {
        if ( savingReorder ) { return; }
        setSavingReorder( true );

        const keys = catsReorderPage.current.getUpdatedKeys();
        const brand = catsReorderPage.current.getSelectedBrandId();

        if ( keys && keys.length > 1 ) {
            const mutationInput = {
                brandId: brand,
                list: keys
            }; 

            await sortCategories( { variables: { input: mutationInput } } );
        }

        setTimeout( () => {
            setShowReorder( false );
            setSavingReorder( false );
        }, 8000 );
    };

    const handleReorderCancelClick = () => {
        setShowReorder( false );
    };
    
    const renderExpandedRow = ( category ) => {
        return (
            <div className="row-expand">
                <div className="flex-container">
                    <div className="flex-left">
                        <strong>Content</strong><br/>
    
                        { ( !category.content && !category.name ) && 
                            <p className="no-translation">No translation for this language</p>
                        }
                                                        
                        { ( category.content || category.name ) && 
                        <Typography.Paragraph ellipsis={ { rows: 6 } }>
                            <span dangerouslySetInnerHTML={ { __html: DOMPurify.sanitize( category.content ) } } /> 
                        </Typography.Paragraph>
                        }
                    </div>

                    <div className="flex-right">
                        <Select
                            bordered={ false }
                            dropdownMatchSelectWidth={ 200 }
                            defaultValue={ category._selectedLocale }
                            onChange={ ( value ) => listPageRef.current.handleLocaleChange( value, category ) }
                            options={ 
                                category.locales.map( l => (
                                    {
                                        key: l.code,
                                        value: l.code,
                                        label: listPageRef.current.getLabelForLocale( l.code )
                                    }
                                ) )
                            }
                            size="small"
                        />
                    </div>
                </div>
                
                { category.courseIds?.length > 0 &&
                    <ListSearchItemsByIds 
                        title="Courses in this Category"
                        ids={ category.courseIds }
                    />
                }

                { category.curriculumIds?.length > 0 &&
                    <ListSearchItemsByIds 
                        ids={ category.curriculumIds }
                        title="Curricula in this Category"
                        type="curricula"
                    />
                }
            </div>
        );
    };

    const tableColumns = [
        {
            dataIndex: 'name',
            key: 'name', 
            title: 'Name',
            sorter: ( a, b ) => 0,
            sortKey: 'name',
            width: 350,
            fixed: 'left',
            render: ( name, i ) => {
                if ( !i.content && !i.name ) {
                    return <span className="no-translation">No translation for this language</span>;
                } else {
                    return (
                        <span className="name-column">
                            { renderWithSearchWords( name, listPageRef.current.getSearchWords() ) }
                        </span> 
                    );
                }
            }
        },
        ...( authService.isHoya() ?
            [
                {
                    dataIndex: 'brandIds',
                    key: 'brands',
                    title: 'Brand',
                    sortKey: 'catBrands',
                    render: ( brandIds ) =>
                        brandIds?.length > 0 ?
                            brandIds.map( i => {
                                const brand = listPageRef.current.getBrand( i );
                                return brand ? <ColoredTag.Brand key={ brand.id } brand={ brand } /> : null;
                            } ) : null
                } ] : [] ),
        {
            dataIndex: 'createdAt',
            key: 'createdAt',
            title: 'Created At',
            sorter: ( a, b ) => 0,
            sortKey: 'createdAt',
            render: ( createdAt ) => <span>{ createdAt }</span>
        },
        {
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            title: 'Updated At',
            sorter: ( a, b ) => 0,
            sortKey: 'updatedAt',
            render: ( updatedAt ) => <span>{ updatedAt }</span>
        },
        {
            title: 'Actions',
            key: 'actions', 
            fixed: 'right', 
            width: '100px',
            render: ( item ) => {
                return (
                    <>
                        <Space>
                            <Button 
                                type="link" 
                                icon={ <EditOutlined /> } 
                                onClick= { () => routeHistory.push( Routes.EditCategory.buildPath( item.id ) ) }
                            />
                            
                            <DeleteButton
                                id={ item.id }   
                                title={ 'Confirm Delete' }
                                deleteCallback={ ( i ) => handleDeleteClick( i ) }
                                confirmMessage= { 
                                    <>
                                        <p>Are you <i>sure</i> you want to permanently delete the category <b>{ item.name }</b>? 
                                        This action cannot be undone.</p>
                                    </>
                                }
                            />
                        </Space>
                    </> 
                );
            }   
        }
    ];   
  
    return (
        <>
            <SearchQueryList 
                ref={ listPageRef }
                type="categories" 
                uiStrings={ uiStrings }
                breadcrumbs={ breadcrumbs }
                tableColumns={ tableColumns }
                isDeleting={ isDeleting }
                extra={ 
                    <Button
                        onClick={ () => handleReorderClick() }
                        type="text"
                    >
                        <OrderedListOutlined />
                        Reorder Categories
                    </Button> 
                }
                renderExpandedRowCallback= { renderExpandedRow }
                createClickCallback={ handleCreateClick }
                setExpandedRowsCallback= { handleSetExpandedRows }
                defaultExpandedRows= { expandedRows }
            />

            { showReorder &&
                <Modal 
                    title="Drag to Reorder" 
                    open={ showReorder } 
                    onOk={ handleReorderSaveClick }
                    onCancel={ handleReorderCancelClick }
                    okText= { 'Ok' }
                    bodyStyle={ { 
                        height: '600px', 
                        overflowX: 'hidden',
                        overflowY: 'scroll' 
                    } }
                >
                    <CategoriesReorder
                        saving={ savingReorder }
                        ref={ catsReorderPage }
                    />
                </Modal>    
            }
        </>
    );
};

export default CategoriesListPage;
